import { FLAGSHIP_ID, getHostel, getTrip, HOSTEL_SLUGS, HOSTELS, type Trip } from "./content";

/**
 * The route builder's state and the rules that change it, as pure functions. The prototype kept this in
 * module globals next to its DOM code; the page keeps it in `routeStore.ts` and renders from it.
 */

export interface RouteStop {
	/** hostel slug */
	s: string;
	/** nights, MIN_NIGHTS to MAX_NIGHTS */
	n: number;
}

export const MIN_NIGHTS = 1;
export const MAX_NIGHTS = 14;

export const stopsOfTrip = (trip: Trip): RouteStop[] => trip.stops.map(([s, n]) => ({ s, n }));

/** what the planner opens with: the banana pancake route, as in the prototype */
export const DEFAULT_ROUTE: RouteStop[] = stopsOfTrip(getTrip(FLAGSHIP_ID)!);

export const inRoute = (route: RouteStop[], slug: string): boolean => route.some((r) => r.s === slug);

/** Cities get two nights, towns and islands three: nobody leaves a beach after two. */
export const defNights = (slug: string): number => (HOSTELS[slug].kind === "city" ? 2 : 3);

/** Add the hostel at the end, or take it out when it is already in: what every "+" and marker tap does. */
export function toggleStop(route: RouteStop[], slug: string): RouteStop[] {
	return inRoute(route, slug) ? route.filter((r) => r.s !== slug) : [...route, { s: slug, n: defNights(slug) }];
}

export const removeStop = (route: RouteStop[], index: number): RouteStop[] => route.filter((_, i) => i !== index);

export function changeNights(route: RouteStop[], index: number, delta: number): RouteStop[] {
	return route.map((r, i) => (i === index ? { ...r, n: Math.max(MIN_NIGHTS, Math.min(MAX_NIGHTS, r.n + delta)) } : r));
}

/** Take the stop at `from` and put it where `to` is: what dragging a row onto another does. */
export function moveStop(route: RouteStop[], from: number, to: number): RouteStop[] {
	if (from === to || !route[from] || !route[to]) return route;
	const next = [...route];
	const [moved] = next.splice(from, 1);
	next.splice(to, 0, moved);
	return next;
}

/** straight-line km between two hostels, rounded: the planner's distance chips */
export function km(a: string, b: string): number {
	const rad = Math.PI / 180;
	const A = HOSTELS[a];
	const B = HOSTELS[b];
	const h = Math.sin(((B.lat - A.lat) * rad) / 2) ** 2 + Math.cos(A.lat * rad) * Math.cos(B.lat * rad) * Math.sin(((B.lon - A.lon) * rad) / 2) ** 2;
	return Math.round(12742 * Math.asin(Math.sqrt(h)));
}

/** The ready-made route's own leg text when one of its legs is exactly a -> b. */
export function knownLeg(trips: Trip[], a: string, b: string): string | null {
	for (const t of trips) {
		for (let i = 0; i < t.stops.length - 1; i++) {
			if (t.stops[i][0] === a && t.stops[i + 1][0] === b) return t.legs[i];
		}
	}
	return null;
}

/** "Next stop ideas": the closest hostels that are not in the route yet. */
export const nearest = (route: RouteStop[], from: string, count = 3): string[] =>
	HOSTEL_SLUGS.filter((k) => k !== from && !inRoute(route, k))
		.sort((x, y) => km(from, x) - km(from, y))
		.slice(0, count);

export const totalNights = (route: RouteStop[]): number => route.reduce((total, r) => total + r.n, 0);

/** First and last day of each stop: [[1, 2], [3, 5], ...] */
export function dayRanges(stops: { n: number }[]): [number, number][] {
	let day = 1;
	return stops.map(({ n }) => {
		const range: [number, number] = [day, day + n - 1];
		day += n;
		return range;
	});
}

/**
 * A route read back from sessionStorage, or null when there is none worth restoring. Unknown slugs
 * (a hostel that left the data) and repeats are dropped and nights are clamped, so a stale or tampered
 * entry can never put the planner in a state it cannot produce itself.
 */
export function parseStoredRoute(raw: string | null): RouteStop[] | null {
	if (raw === null) return null;
	try {
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) return null;
		const seen = new Set<string>();
		const route: RouteStop[] = [];
		for (const item of parsed) {
			const s = item?.s;
			const n = Math.round(Number(item?.n));
			if (typeof s !== "string" || !getHostel(s) || seen.has(s) || !Number.isFinite(n)) continue;
			seen.add(s);
			route.push({ s, n: Math.max(MIN_NIGHTS, Math.min(MAX_NIGHTS, n)) });
		}
		return route;
	} catch {
		return null;
	}
}
